import React, { useState } from 'react';
import { IoAdd } from 'react-icons/io5';
import { useAuth } from '@/features/auth/context/AuthContext';
import { useAdminPanel } from '@/hooks/useAdminPanel';
import UniversitySidebar from '@/components/AdminPanel/UniversitySidebar';
import UniversityDetailsForm from '@/components/AdminPanel/UniversityDetailsForm';
import UniversitySupplements from '@/components/AdminPanel/UniversitySupplements';
import AIModelGuidance from '@/components/AdminPanel/AIModelGuidance';
import UniversityCreateModal from '@/components/AdminPanel/UniversityCreateModal';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const {
    universities,
    selectedUniversity,
    universitiesLoading,
    detailsLoading,
    saving,
    searchQuery,
    formData,
    supplements,
    hasNextPage,
    totalUniversities,
    isLoadingMore,
    promptCategories,
    saveChanges,
    handleInputChange,
    handleLoadMore,
    handleSearchChange,
    handleUniversitySelect,
    handleSupplementCreate,
    handleSupplementUpdate,
    handleSupplementDelete,
    handleUniversityCreate,
    handleUniversityDelete,
  } = useAdminPanel();

  if (!user || user.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <UniversitySidebar
        universities={universities}
        selectedUniversityId={selectedUniversity?.id || null}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onUniversitySelect={handleUniversitySelect}
        universitiesLoading={universitiesLoading}
        totalUniversities={totalUniversities}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedUniversity ? selectedUniversity.name : 'Select a University'}
              </h2>
              <p className="text-sm text-gray-500">Manage university details and AI model guidance</p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              {IoAdd({ className: "text-lg" })}
              <span>Add University</span>
            </button>
          </div>
        </div>
        
        <div className="flex-1 p-8 overflow-y-auto" style={{ paddingBottom: '7rem' }}>
          {detailsLoading ? (
            <div className="bg-white rounded-lg shadow-lg">
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Loading university details...</p>
              </div>
            </div>
          ) : selectedUniversity ? (
            <div>
              <UniversityDetailsForm
                university={selectedUniversity}
                formData={formData}
                onInputChange={handleInputChange}
                onSave={saveChanges}
                onDelete={handleUniversityDelete}
                saving={saving}
              />
              <UniversitySupplements 
                supplements={supplements} 
                onSupplementUpdate={handleSupplementUpdate}
                onSupplementCreate={handleSupplementCreate}
                onSupplementDelete={handleSupplementDelete}
                promptCategories={promptCategories}
                universityId={selectedUniversity.id}
              />
              <AIModelGuidance
                university={selectedUniversity}
                formData={formData}
                onInputChange={handleInputChange}
                onSave={saveChanges}
                saving={saving}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg">
                <p>Select a university from the sidebar to start editing</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <UniversityCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleUniversityCreate}
      />
    </div>
  );
};

export default AdminPanel;